import React, {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import CreditCard from "@mui/icons-material/CreditCard";
import Today from "@mui/icons-material/Today";
import Event from "@mui/icons-material/Event";
import { LinearProgress, styled, Typography, Card, CardHeader, CardContent } from "@mui/material";

import useDataSource from "../../../hooks/useDataSource";
import {
    formatNumber
} from "../../../utilities/FormatUtilities";
import * as DateUtilities from "../../../utilities/DateUtilities";

const PREFIX = 'BudgetDates';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: '100%',
        width: '100%'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    }
}));

export default function BudgetDates({configuration}) {


    const { t, i18n } = useTranslation();

    const title = configuration.name?.title ? configuration.name.title : '';
    const subtitle = configuration.name?.subtitle ? configuration.name.subtitle : '';
    const dataSourceName = configuration.dataSource?.name ? configuration.dataSource?.name : '';
    const card = configuration.element?.card ? configuration.element?.card : null;
    const budgetTitle = configuration.dataSource?.fields?.budgetTitle ? configuration.dataSource?.fields?.budgetTitle : '';
    const budgetField = configuration.dataSource?.fields?.budgetField ? configuration.dataSource?.fields?.budgetField : '';
    const startTitle = configuration.dataSource?.fields?.startTitle ? configuration.dataSource?.fields?.startTitle : '';
    const startField = configuration.dataSource?.fields?.startField ? configuration.dataSource?.fields?.startField : '';
    const endTitle = configuration.dataSource?.fields?.endTitle ? configuration.dataSource?.fields?.endTitle : '';
    const endField = configuration.dataSource?.fields?.endField ? configuration.dataSource?.fields?.endField : '';

    // App State
    const userDataSource = useDataSource(dataSourceName);

    // Component State
    const [budget, setBudget] = useState('-');
    const [startDate, setStartDate] = useState('-');
    const [endDate, setEndDate] = useState('-');
    const [showProgress, setShowProgress] = useState(configuration.element?.showFetching !== undefined ? configuration.element?.showFetching : true);

    // Data
    useEffect(() => {
        // console.log('BudgetDates: useEffect([userDataSource.data])', userDataSource.data);

        if (userDataSource.data && userDataSource.fetched && userDataSource.data.length > 0) {

            setBudget(formatNumber(userDataSource.data[0][budgetField], {
                abbreviate: true,
                addSeparators: false,
                currency: true,
                decimals: 1,
                default: '--',
                fixDecimals: true,
                round: true
            }));
            setStartDate(DateUtilities.dateMonthYearString(userDataSource.data[0][startField]));
            setEndDate(DateUtilities.dateMonthYearString(userDataSource.data[0][endField]));

        } else {
            setBudget('--');
            setStartDate('--');
            setEndDate('--');

        }
    }, [userDataSource.data]);

    return (
        <Root className={classes.root}>
            {showProgress && userDataSource.fetching &&
                <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !userDataSource.fetching &&
                <div className={classes.linearProgress}></div>
            }
            <Card style={{boxShadow: 'none', height: '100%', ...card}}>
                <CardHeader style={{ height: '68px' }}
                            titleTypographyProps={{color: 'textPrimary', variant: 'subtitle2'}}
                            title={t(title)}
                            subheaderTypographyProps={{color: 'textSecondary', variant: 'body2'}}
                            subheader={t(subtitle)}></CardHeader>
                <CardContent style={{height: 'calc(100% - 68px)'}}>
                    <Typography variant="caption" display="block" gutterBottom align="center">
                        {budgetTitle}
                    </Typography>
                    <Typography variant="h6" display="block" gutterBottom style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <CreditCard/> {budget}
                    </Typography>
                    <Typography variant="caption" display="block" gutterBottom align="center">
                        {t(startTitle)}
                    </Typography>
                    <Typography variant="h6" display="block" gutterBottom style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <Today/> {startDate}
                    </Typography>
                    <Typography variant="caption" display="block" gutterBottom align="center">
                        {t(endTitle)}
                    </Typography>
                    <Typography variant="h6" display="block" gutterBottom style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <Event/> {endDate}
                    </Typography>
                </CardContent>
            </Card>

        </Root>
    );
}
